import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Download, FileText, Loader2 } from 'lucide-react';
import { getCosmeticReportList, getCosmeticSkincareStatus, getOrderMedicineUrl } from './CosmeticSkinCareApi';
import { resolvePdfUrl } from '../../api/Services/resolvePdfUrl';

const CONDITION_COLORS = {
  acne: '#E85D4C',
  acne_scars: '#C0392B',
  pigmentation: '#F0A73D',
  hyperpigmentation: '#F0A73D',
  dullness: '#5B8DEF',
  dull_skin: '#5B8DEF',
  pores: '#4CAF7D',
  large_pores: '#4CAF7D',
  wrinkles: '#9B59B6',
  fine_lines: '#9B59B6',
  dark_circles: '#6C63FF',
  redness: '#E91E8C',
};
const FALLBACK_COLORS = ['#E85D4C', '#F0A73D', '#5B8DEF', '#4CAF7D', '#9B59B6', '#6C63FF'];

const CONFIDENCE_META = {
  High: { color: '#14804A', bg: '#E4F9EC' },
  Medium: { color: '#B8860B', bg: '#FFF3D9' },
  Low: { color: '#C0392B', bg: '#FDEAEA' },
};

const STATUS_META = {
  Improving: { color: '#14804A', bg: '#E4F9EC' },
  'First Detected': { color: '#B8860B', bg: '#FFF3D9' },
  'Check next scan': { color: '#5B8DEF', bg: '#EAF1FF' },
};

const humanize = (key) => (key || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const getColor = (key, i) => CONDITION_COLORS[key] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '');
const formatMonthYear = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : '');

const CosmeticPreviousReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);
  const [error, setError] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFilter, setDateFilter] = useState('');
  // Scans that are uploaded but not finished yet: requestId -> { createdDate, confirmed }.
  // "confirmed" = the status API has said it's still processing (only those are shown).
  const [pending, setPending] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  const settledRef = useRef(new Set()); // ids the status API reported done/failed — never re-added as pending

  useEffect(() => {
    const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');
    const patientid = loginObj?.code;
    if (!patientid) {
      setError('Please log in to view your past reports.');
      setReports([]);
      return;
    }
    getCosmeticReportList({ patientid, pageSize: 50 })
      .then((res) => {
        const all = res.data?.data || [];
        const rows = all.filter((r) => r.STATUS === 1 && r.OVERALL_SKIN_SCORE != null);
        setReports(rows);

        // Rows the list already returns as queued/processing (STATUS 0 or 3)
        const nextPending = {};
        all.forEach((r) => {
          const status = Number(r.STATUS);
          if (r.REQUESTID && status !== 1 && status !== 2 && !settledRef.current.has(r.REQUESTID)) {
            nextPending[r.REQUESTID] = { createdDate: r.CREATEDDATE, confirmed: true };
          }
        });
        // The scan just uploaded in this session may not be in get_report_list yet —
        // track it too; it's shown once the status API confirms it's still processing.
        let sessionRequestId = null;
        try { sessionRequestId = sessionStorage.getItem('cosmeticRequestId'); } catch {}
        if (
          sessionRequestId &&
          !nextPending[sessionRequestId] &&
          !settledRef.current.has(sessionRequestId) &&
          !all.some((r) => r.REQUESTID === sessionRequestId)
        ) {
          nextPending[sessionRequestId] = { createdDate: null, confirmed: false };
        }
        setPending(nextPending);
      })
      .catch(() => {
        setError('Could not load your past reports. Please try again later.');
        setReports([]);
      });
  }, [reloadKey]);

  // ── Live-refresh pending scans ──────────────────────────────────────────
  // Poll skincare-cosmetic-status for each pending scan; once any finishes,
  // reload the list so its full report card appears without a manual refresh.
  const pendingIds = Object.keys(pending).sort().join(',');
  useEffect(() => {
    if (!pendingIds) return undefined;
    const patientid = JSON.parse(localStorage.getItem('loginObj') || 'null')?.code;
    if (!patientid) return undefined;

    const POLL_MS = 9000;
    const MAX_ATTEMPTS = 20; // ~3 min, same budget as the Skin Care poll on AI Reports
    let cancelled = false;
    let attempts = 0;
    let timer;

    const poll = async () => {
      if (cancelled) return;
      if (!document.hidden) { // skip ticks while the tab is backgrounded
        attempts += 1;
        const results = await Promise.all(
          pendingIds.split(',').map(async (id) => {
            try {
              const res = await getCosmeticSkincareStatus(id, patientid);
              return { id, status: Number(res.data?.STATUS) };
            } catch {
              return { id, status: null };
            }
          })
        );
        if (cancelled) return;

        const finished = results.filter((r) => r.status === 1 || r.status === 2);
        if (finished.length) {
          finished.forEach((r) => settledRef.current.add(r.id));
          setReloadKey((k) => k + 1); // reload list → effect re-runs with what's still pending
          return;
        }
        const processing = results.filter((r) => r.status === 0 || r.status === 3).map((r) => r.id);
        if (processing.length) {
          setPending((prev) => {
            const next = { ...prev };
            processing.forEach((id) => { if (next[id]) next[id] = { ...next[id], confirmed: true }; });
            return next;
          });
        }
      }
      if (!cancelled && attempts < MAX_ATTEMPTS) timer = setTimeout(poll, POLL_MS);
    };

    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [pendingIds]);

  const pendingList = Object.entries(pending).filter(([, p]) => p.confirmed);

  // Chronological (oldest -> newest) view, used purely to compute trend per condition.
  const chronological = useMemo(
    () => [...(reports || [])].sort((a, b) => new Date(a.CREATEDDATE) - new Date(b.CREATEDDATE)),
    [reports]
  );

  const conditionHistory = useMemo(() => {
    const map = {};
    chronological.forEach((report) => {
      (report.DETECTED_CONDITIONS || []).forEach((c) => {
        if (!map[c.condition]) map[c.condition] = [];
        map[c.condition].push({ date: report.CREATEDDATE, pct: c.confidence_percent });
      });
    });
    return map;
  }, [chronological]);

  const getStatusFor = (condition, reportDate) => {
    const history = conditionHistory[condition] || [];
    const idx = history.findIndex((h) => h.date === reportDate);
    if (idx <= 0) return 'First Detected';
    const prev = history[idx - 1];
    const curr = history[idx];
    if (curr.pct == null || prev.pct == null) return 'First Detected';
    return curr.pct < prev.pct ? 'Improving' : 'Check next scan';
  };

  const summary = useMemo(() => {
    if (!chronological.length) return null;
    const latest = chronological[chronological.length - 1];
    const distinctConditions = new Set();
    chronological.forEach((r) => (r.DETECTED_CONDITIONS || []).forEach((c) => distinctConditions.add(c.condition)));

    let improved = null;
    let bestDelta = 0;
    Object.entries(conditionHistory).forEach(([condition, history]) => {
      if (history.length < 2) return;
      const delta = history[history.length - 1].pct - history[0].pct;
      if (delta < bestDelta) {
        bestDelta = delta;
        improved = condition;
      }
    });

    const latestConditions = (latest.DETECTED_CONDITIONS || []).slice().sort((a, b) => (b.confidence_percent || 0) - (a.confidence_percent || 0));
    const stillExperienced = latestConditions.find((c) => c.condition !== improved) || latestConditions[0];

    return {
      totalScore: latest.OVERALL_SKIN_SCORE,
      sinceLabel: formatMonthYear(chronological[0].CREATEDDATE),
      problemsDetected: distinctConditions.size,
      improvedLabel: improved ? humanize(improved) : '—',
      stillExperiencedLabel: stillExperienced ? humanize(stillExperienced.condition) : '—',
    };
  }, [chronological, conditionHistory]);

  const sortedReports = useMemo(() => {
    let list = [...(reports || [])];
    if (dateFilter) {
      list = list.filter((r) => r.CREATEDDATE && r.CREATEDDATE.slice(0, 10) === dateFilter);
    }
    list.sort((a, b) => {
      const diff = new Date(b.CREATEDDATE) - new Date(a.CREATEDDATE);
      return sortOrder === 'newest' ? diff : -diff;
    });
    return list;
  }, [reports, sortOrder, dateFilter]);

  const handleBack = () => navigate('/cosmetic-skincare-analysis');
  const handleStartNewScan = () => {
    // Starting a brand-new scan — clear any leftover images/requestId from a
    // previous session so they don't leak into this one.
    try {
      sessionStorage.removeItem('cosmeticFaceImages');
      sessionStorage.removeItem('cosmeticRequestId');
      sessionStorage.removeItem('cosmeticResultsReturnPath');
    } catch {}
    navigate('/cosmetic-skincare-analysis/face-scan');
  };

  const handleViewFullReport = (report) => {
    try {
      sessionStorage.setItem('cosmeticResultRecord', JSON.stringify(report));
      // Remember exactly where this report was opened from (this page's own
      // route, or wherever it's embedded, e.g. the AI Reports "Cosmetic
      // Skincare" tab) so the Results page's back button can return here
      // reliably instead of depending on the browser history stack.
      sessionStorage.setItem('cosmeticResultsReturnPath', window.location.pathname);
    } catch {}
    navigate('/cosmetic-skincare-analysis/results');
  };

  const handleDownloadPdf = async (report) => {
    if (!report.PDFREPORT) return;
    const resolvedUrl = await resolvePdfUrl(report.PDFREPORT);
    if (resolvedUrl) window.open(resolvedUrl, '_blank', 'noopener');
  };

  return (
    <div className="mx-auto w-full">
      <style>{`
        .pr-wrapper {
          background-color: #fff; background-image: var(--section-bg-gradient);
          padding: 24px 40px 64px 40px;
          min-height: 70vh;
        }

        .pr-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .pr-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 700;
          font-size: 16px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
        }

        .pr-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 13px;
          color: #ffffff;
          background: var(--color-brand-primary);
          border: none;
          border-radius: 999px;
          padding: 9px 20px;
          cursor: pointer;
        }

        .pr-summary-card {
          display: flex;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0px 2px 10px 0px #00000010;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .pr-summary-tile {
          flex: 1;
          padding: 16px 20px;
          border-right: 1px solid #DFF1FB;
        }

        .pr-summary-tile:last-child { border-right: none; }

        .pr-summary-label {
          font-size: 10px;
          font-weight: 700;
          color: #9490A8;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 6px;
        }

        .pr-summary-value {
          font-size: 20px;
          font-weight: 700;
          color: var(--color-brand-primary);
        }

        .pr-summary-caption {
          font-size: 11px;
          color: #676C80;
          margin-top: 2px;
        }

        .pr-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .pr-toolbar-title {
          font-weight: 700;
          font-size: 15px;
          color: var(--color-brand-primary);
        }

        .pr-toolbar-controls {
          display: flex;
          gap: 10px;
        }

        .pr-select, .pr-date {
          border: 1px solid #D9D6E3;
          border-radius: 999px;
          padding: 7px 14px;
          font-size: 12px;
          color: var(--color-text-subheading);
          background: #ffffff;
        }

        /* Mobile WebKit/Blink date inputs render their internal dd/mm/yyyy
           segments with their own default (often near-invisible) color that
           the input's own color property doesn't override. */
        .pr-date::-webkit-datetime-edit,
        .pr-date::-webkit-datetime-edit-fields-wrapper,
        .pr-date::-webkit-datetime-edit-text,
        .pr-date::-webkit-datetime-edit-day-field,
        .pr-date::-webkit-datetime-edit-month-field,
        .pr-date::-webkit-datetime-edit-year-field {
          color: var(--color-text-subheading);
        }

        .pr-report-card {
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0px 2px 10px 0px #00000010;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .pr-report-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
          padding: 14px 20px;
          background: linear-gradient(282.08deg, #DFF1FB 2.51%, #FFFFFF 100.3%);
        }

        .pr-report-date-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .pr-pending-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: #B8860B;
          background: #FFF3D9;
          border-radius: 999px;
          padding: 6px 12px;
        }

        .pr-report-date {
          font-weight: 700;
          font-size: 14px;
          color: #1A1A2E;
        }

        .pr-report-tag {
          font-size: 10px;
          font-weight: 700;
          color: var(--color-brand-primary);
          background: #EAF3FA;
          border-radius: 999px;
          padding: 3px 10px;
        }

        .pr-report-actions {
          display: flex;
          gap: 8px;
        }

        .pr-btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 12px;
          background: #ffffff;
          color: var(--color-brand-primary);
          border: 1.5px solid #D9D6E3;
          border-radius: 999px;
          padding: 7px 14px;
          cursor: pointer;
        }

        .pr-table-wrap { overflow-x: auto; }

        .pr-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .pr-table th {
          text-align: left; color: #9490A8; font-weight: 600; font-size: 10px;
          text-transform: uppercase; letter-spacing: 0.3px;
          padding: 10px 20px; border-bottom: 1px solid #EAF3FA;
        }
        .pr-table td { padding: 12px 20px; border-bottom: 1px solid #F1F8FD; color: #1A1A2E; }
        .pr-product-link { color: #1A1A2E; font-weight: 600; text-decoration: none; }
        .pr-product-link:hover { color: var(--color-brand-primary); text-decoration: underline; }
        .pr-table tr:last-child td { border-bottom: none; }

        .pr-problem-cell { display: flex; align-items: flex-start; gap: 8px; font-weight: 600; }
        .pr-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 5px; }
        .pr-problem-sub { font-size: 10px; font-weight: 500; color: #9490A8; text-transform: capitalize; margin-top: 2px; }

        .pr-status-pill {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 11px; font-weight: 700; border-radius: 999px; padding: 3px 10px;
          white-space: nowrap;
        }

        .pr-empty, .pr-loading {
          text-align: center;
          padding: 60px 20px;
          color: #676C80;
          font-size: 13px;
        }

        .pr-spin {
          animation: pr-spin 0.8s linear infinite;
        }

        @keyframes pr-spin {
          to { transform: rotate(360deg); }
        }

        @media (max-width: 900px) {
          .pr-summary-card { flex-wrap: wrap; }
          .pr-summary-tile { flex: 1 1 50%; border-bottom: 1px solid #DFF1FB; }
        }

        @media (max-width: 768px) {
          .pr-wrapper { padding: 16px; }
          .pr-header { flex-wrap: wrap; gap: 10px; }
          .pr-toolbar-controls { flex-wrap: wrap; }
          .pr-report-actions { flex-wrap: wrap; }
        }

        @media (max-width: 480px) {
          .pr-wrapper { padding: 12px; }
          .pr-btn-primary { width: 100%; justify-content: center; }
          .pr-report-header { flex-direction: column; align-items: stretch; }
          .pr-report-actions .pr-btn-primary,
          .pr-report-actions .pr-btn-outline { flex: 1 1 auto; justify-content: center; }
          .pr-toolbar-controls { width: 100%; }
          .pr-select, .pr-date { flex: 1 1 auto; }
        }
      `}</style>

      <div className="pr-wrapper">
        <div className="pr-header">
          <button className="pr-back" onClick={handleBack}>
            <ChevronLeft size={20} />
            My Skin Reports
          </button>
          <button className="pr-btn-primary" onClick={handleStartNewScan}>
            Start New Scan
          </button>
        </div>

        {reports === null && (
          <div className="pr-loading">
            <Loader2 size={20} className="pr-spin" style={{ marginBottom: 8 }} />
            <div>Loading your reports…</div>
          </div>
        )}

        {pendingList.map(([id, p]) => (
          <div className="pr-report-card" key={`pending-${id}`}>
            <div className="pr-report-header">
              <div className="pr-report-date-row">
                <span className="pr-report-date">{p.createdDate ? formatDate(p.createdDate) : 'Just now'}</span>
                <span className="pr-report-tag">Cosmetic Analysis</span>
              </div>
              <span className="pr-pending-pill">
                <Loader2 size={13} className="pr-spin" />
                Report Pending
              </span>
            </div>
            <div className="pr-empty">Your skin report is being generated. It will appear here automatically once it's ready.</div>
          </div>
        ))}

        {reports !== null && reports.length === 0 && pendingList.length === 0 && (
          <div className="pr-empty">
            {error || "You don't have any completed skin reports yet."}
          </div>
        )}

        {summary && (
          <div className="pr-summary-card">
            <div className="pr-summary-tile">
              <div className="pr-summary-label">Total Score</div>
              <div className="pr-summary-value">{summary.totalScore}/10</div>
              <div className="pr-summary-caption">Since {summary.sinceLabel}</div>
            </div>
            <div className="pr-summary-tile">
              <div className="pr-summary-label">Problems Detected</div>
              <div className="pr-summary-value">{summary.problemsDetected}</div>
              <div className="pr-summary-caption">Across all scans</div>
            </div>
            <div className="pr-summary-tile">
              <div className="pr-summary-label">Problems Improved</div>
              <div className="pr-summary-value" style={{ fontSize: 16 }}>{summary.improvedLabel}</div>
              <div className="pr-summary-caption">Visibly better and full</div>
            </div>
            <div className="pr-summary-tile">
              <div className="pr-summary-label">Still Experienced</div>
              <div className="pr-summary-value" style={{ fontSize: 16 }}>{summary.stillExperiencedLabel}</div>
              <div className="pr-summary-caption">Needs consistent care</div>
            </div>
          </div>
        )}

        {reports && reports.length > 0 && (
          <>
            <div className="pr-toolbar">
              <div className="pr-toolbar-title">All Reports</div>
              <div className="pr-toolbar-controls">
                <select className="pr-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
                <input
                  type="date"
                  className="pr-date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                />
                {dateFilter && (
                  <button className="pr-btn-outline" onClick={() => setDateFilter('')}>Clear</button>
                )}
              </div>
            </div>

            {sortedReports.map((report) => {
              const conditions = report.DETECTED_CONDITIONS || [];
              return (
                <div className="pr-report-card" key={report.REQUESTID}>
                  <div className="pr-report-header">
                    <div className="pr-report-date-row">
                      <span className="pr-report-date">{formatDate(report.CREATEDDATE)}</span>
                      <span className="pr-report-tag">Cosmetic Analysis</span>
                    </div>
                    <div className="pr-report-actions">
                      <button className="pr-btn-primary" onClick={() => handleViewFullReport(report)}>
                        <FileText size={13} />
                        View Full Report
                      </button>
                      <button className="pr-btn-outline" onClick={() => handleDownloadPdf(report)} disabled={!report.PDFREPORT}>
                        <Download size={13} />
                        Download PDF
                      </button>
                    </div>
                  </div>

                  {conditions.length === 0 ? (
                    <div className="pr-empty">No specific concerns detected in this scan.</div>
                  ) : (
                    <div className="pr-table-wrap">
                      <table className="pr-table">
                        <thead>
                          <tr>
                            <th>Problem</th>
                            <th>Recommended Product</th>
                            <th>Key Ingredient</th>
                            <th>Confidence</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {conditions.map((c, i) => {
                            // Prefer recommendations tied to this exact detected condition; if the
                            // model didn't return any (e.g. only questionnaire-based ones came back
                            // under a different condition key), fall back to those as 2nd priority
                            // instead of showing nothing.
                            const products = report.PRODUCT_RECOMMENDATIONS?.[c.condition]?.length
                              ? report.PRODUCT_RECOMMENDATIONS[c.condition]
                              : (Object.values(report.PRODUCT_RECOMMENDATIONS || {}).find((arr) => arr?.length) || []);
                            const ingredients = report.INGREDIENT_RECOMMENDATIONS?.[c.condition]?.length
                              ? report.INGREDIENT_RECOMMENDATIONS[c.condition]
                              : (Object.values(report.INGREDIENT_RECOMMENDATIONS || {}).find((arr) => arr?.length) || []);
                            const status = getStatusFor(c.condition, report.CREATEDDATE);
                            const statusMeta = STATUS_META[status];
                            const productConfidence = products[0]?.confidence;
                            const confidenceMeta = CONFIDENCE_META[productConfidence];
                            return (
                              <tr key={c.condition}>
                                <td>
                                  <span className="pr-problem-cell">
                                    <span className="pr-dot" style={{ background: getColor(c.condition, i) }} />
                                    <span>
                                      <div>{c.label || humanize(c.condition)}</div>
                                      <div className="pr-problem-sub">
                                        {c.confidence_percent != null ? `${c.confidence_percent}% match (${c.source})` : `Detected (${c.source})`}
                                      </div>
                                    </span>
                                  </span>
                                </td>
                                <td>
                                  {products[0]?.drug_name ? (
                                    <a
                                      className="pr-product-link"
                                      href={getOrderMedicineUrl(products[0].drug_name)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {products[0].drug_name}
                                    </a>
                                  ) : '—'}
                                </td>
                                <td>{ingredients[0] || '—'}</td>
                                <td>
                                  {productConfidence ? (
                                    <span className="pr-status-pill" style={{ color: confidenceMeta.color, background: confidenceMeta.bg }}>
                                      {productConfidence}
                                    </span>
                                  ) : '—'}
                                </td>
                                <td>
                                  <span className="pr-status-pill" style={{ color: statusMeta.color, background: statusMeta.bg }}>
                                    {status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default CosmeticPreviousReports;
